import React, { useState, useEffect, useMemo } from 'react'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { usePOSStore } from '@/store/posStore'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { EXPENSE_CATEGORIES } from '@/types/pos'
import type { Expense } from '@/types/pos'

export const ExpensesPage: React.FC = () => {
  const {
    expenses,
    isLoading,
    fetchExpenses,
    addExpense,
    updateExpense,
    deleteExpense
  } = usePOSStore()

  const [showAddForm, setShowAddForm] = useState(false)
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('All')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { showSuccess, showError } = useNotificationActions()

  useEffect(() => {
    fetchExpenses()
  }, [fetchExpenses])

  const setQuickDate = (preset: 'today' | 'week' | 'month' | 'all') => {
    const now = new Date()
    if (preset === 'all') {
      setDateFrom('')
      setDateTo('')
    } else if (preset === 'today') {
      const today = now.toISOString().slice(0, 10)
      setDateFrom(today)
      setDateTo(today)
    } else if (preset === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      setDateFrom(weekAgo.toISOString().slice(0, 10))
      setDateTo(now.toISOString().slice(0, 10))
    } else if (preset === 'month') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      setDateFrom(monthStart.toISOString().slice(0, 10))
      setDateTo(now.toISOString().slice(0, 10))
    }
  }

  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const matchesSearch = (expense.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (expense.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (expense.reference || '').toLowerCase().includes(searchTerm.toLowerCase())

      const matchesCat = filterCategory === 'All' || expense.category === filterCategory

      let matchesDate = true
      if (dateFrom || dateTo) {
        const expDate = new Date(expense.date || expense.createdAt as string)
        const expDateStr = expDate.toISOString().slice(0, 10)
        if (dateFrom && expDateStr < dateFrom) matchesDate = false
        if (dateTo && expDateStr > dateTo) matchesDate = false
      }

      return matchesSearch && matchesCat && matchesDate
    })
  }, [expenses, searchTerm, filterCategory, dateFrom, dateTo])

  const totalFiltered = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0)
  const totalAll = expenses.reduce((sum, e) => sum + (e.amount || 0), 0)

  // Category breakdown
  const categoryBreakdown = useMemo(() => {
    const map: Record<string, { total: number; count: number }> = {}
    filteredExpenses.forEach(e => {
      const cat = e.category || 'General'
      if (!map[cat]) map[cat] = { total: 0, count: 0 }
      map[cat].total += e.amount || 0
      map[cat].count += 1
    })
    return Object.entries(map)
      .map(([category, data]) => ({ category, ...data }))
      .sort((a, b) => b.total - a.total)
  }, [filteredExpenses])

  const todayTotal = expenses.filter(e => {
    const d = new Date(e.date || e.createdAt as string)
    return d.toDateString() === new Date().toDateString()
  }).reduce((sum, e) => sum + (e.amount || 0), 0)

  const monthTotal = expenses.filter(e => {
    const d = new Date(e.date || e.createdAt as string)
    const now = new Date()
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
  }).reduce((sum, e) => sum + (e.amount || 0), 0)

  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    const formData = new FormData(e.target as HTMLFormElement)
    const data = {
      title: formData.get('title') as string,
      amount: parseFloat(formData.get('amount') as string),
      category: formData.get('category') as string,
      date: formData.get('date') as string || new Date().toISOString().split('T')[0],
      paymentMethod: formData.get('paymentMethod') as string,
      reference: formData.get('reference') as string,
      notes: formData.get('notes') as string,
    }

    try {
      await addExpense(data)
      showSuccess('Expense Added', `${data.title} - Rs.${data.amount.toLocaleString()} recorded.`)
      setShowAddForm(false)
    } catch (error) {
      showError('Failed', 'Could not save expense.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditExpense = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingExpense) return
    setIsSubmitting(true)
    const formData = new FormData(e.target as HTMLFormElement)
    const updates = {
      title: formData.get('title') as string,
      amount: parseFloat(formData.get('amount') as string),
      category: formData.get('category') as string,
      date: formData.get('date') as string,
      paymentMethod: formData.get('paymentMethod') as string,
      reference: formData.get('reference') as string,
      notes: formData.get('notes') as string,
    }
    try {
      await updateExpense(editingExpense.id || editingExpense._id!, updates)
      showSuccess('Expense Updated', 'Changes saved successfully.')
      setEditingExpense(null)
    } catch (error) {
      showError('Update Failed', 'Could not update expense.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (expense: Expense) => {
    if (!confirm(`Delete expense "${expense.title}"?`)) return
    try {
      await deleteExpense(expense.id || expense._id!)
      showSuccess('Deleted', `${expense.title} has been removed.`)
    } catch (error) {
      showError('Delete Failed', 'Could not delete expense.')
    }
  }

  const handleExportCSV = () => {
    if (filteredExpenses.length === 0) {
      showError('No data', 'No expenses to export.')
      return
    }
    const headers = ['Title', 'Category', 'Amount', 'Payment Method', 'Date', 'Reference', 'Notes']
    const rows = filteredExpenses.map(e => [
      `"${(e.title || '').replace(/"/g, '""')}"`,
      e.category || '',
      (e.amount || 0).toFixed(2),
      e.paymentMethod || '',
      e.date ? new Date(e.date).toLocaleDateString() : '',
      `"${(e.reference || '').replace(/"/g, '""')}"`,
      `"${(e.notes || '').replace(/"/g, '""')}"`,
    ])
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `expenses-${dateFrom || 'all'}-to-${dateTo || 'all'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const renderForm = (expense: Expense | null, onSubmit: (e: React.FormEvent) => void, onCancel: () => void) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="glass-card w-full max-w-lg border-primary/30 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold esales-text mb-6">
          {expense ? 'Edit Expense' : 'Record New Expense'}
        </h2>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Title / Description *</label>
              <input name="title" required defaultValue={expense?.title || ''} placeholder="e.g. Electricity Bill" className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Amount (Rs.) *</label>
              <input name="amount" type="number" step="0.01" min="0.01" required defaultValue={expense?.amount || ''} placeholder="0.00" className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none text-lg font-bold" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Category</label>
              <select name="category" defaultValue={expense?.category || 'General'} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none">
                {EXPENSE_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Date</label>
              <input name="date" type="date" defaultValue={expense?.date?.split('T')[0] || new Date().toISOString().split('T')[0]} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Payment Method</label>
              <select name="paymentMethod" defaultValue={expense?.paymentMethod || 'cash'} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none">
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Reference / Receipt #</label>
              <input name="reference" defaultValue={expense?.reference || ''} placeholder="Optional" className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Notes</label>
              <input name="notes" defaultValue={expense?.notes || ''} placeholder="Additional details..." className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <button type="button" onClick={onCancel} className="flex-1 py-4 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="flex-1 py-4 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest glow-effect transition-colors disabled:opacity-50">
              {isSubmitting ? 'SAVING...' : expense ? 'SAVE CHANGES' : 'RECORD EXPENSE'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gradient-space p-3 sm:p-4 lg:p-6">
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold esales-text font-display">Expenses</h1>
            <p className="text-muted-foreground mt-2">Track and manage business expenses.</p>
          </div>
          <button onClick={() => setShowAddForm(true)} className="glow-button">
            <span className="mr-2">💸</span> Record Expense
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Total Expenses', value: `Rs.${totalAll.toLocaleString()}`, icon: '💰', color: 'text-red-400' },
            { label: "Today's Expenses", value: `Rs.${todayTotal.toLocaleString()}`, icon: '📅', color: 'text-yellow-400' },
            { label: 'This Month', value: `Rs.${monthTotal.toLocaleString()}`, icon: '📊', color: 'text-blue-400' },
            { label: 'Total Records', value: expenses.length, icon: '📋', color: 'text-primary' },
          ].map((stat, i) => (
            <div key={i} className="glass-card">
              <div className="flex justify-between items-start mb-2">
                <span className="text-2xl">{stat.icon}</span>
                <span className={`text-2xl font-bold ${stat.color}`}>{stat.value}</span>
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-widest font-bold">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Category Breakdown */}
        {categoryBreakdown.length > 0 && (
          <div className="glass-card mb-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Expense Breakdown</h3>
            <div className="flex flex-wrap gap-3">
              {categoryBreakdown.map(cat => (
                <div key={cat.category} className="flex items-center gap-2 px-3 py-2 bg-muted/30 border border-border/50 rounded-lg">
                  <span className="text-sm font-medium">{cat.category}</span>
                  <span className="text-xs font-bold text-red-400">Rs.{cat.total.toLocaleString()}</span>
                  <span className="text-[10px] text-muted-foreground">({cat.count})</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="glass-card mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            <div className="col-span-2 sm:col-span-3 md:col-span-2">
              <input
                type="text"
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 bg-background/50 border border-border rounded-lg placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="col-span-2 sm:col-span-1 px-4 py-2 bg-background/50 border border-border rounded-lg"
            >
              <option value="All">All Categories</option>
              {EXPENSE_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <div className="grid grid-cols-1 sm:grid-cols-2 col-span-2 sm:col-span-3 md:col-span-2 gap-4">
              <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-4 py-2 bg-background/50 border border-border rounded-lg text-sm" placeholder="From" />
              <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} className="w-full px-4 py-2 bg-background/50 border border-border rounded-lg text-sm" placeholder="To" />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between mt-3 gap-2">
            <div className="flex gap-2">
              {[
                { label: 'All', value: 'all' as const },
                { label: 'Today', value: 'today' as const },
                { label: 'This Week', value: 'week' as const },
                { label: 'This Month', value: 'month' as const },
              ].map(preset => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setQuickDate(preset.value)}
                  className="px-3 py-1 text-xs font-semibold rounded-lg bg-muted/50 hover:bg-muted/80 text-muted-foreground transition-colors"
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Showing: </span>
              <span className="text-sm font-bold text-red-400">Rs.{totalFiltered.toLocaleString()}</span>
              <span className="text-sm text-muted-foreground">({filteredExpenses.length} records)</span>
              <button
                type="button"
                onClick={handleExportCSV}
                className="px-4 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 transition-colors"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Expense List */}
        <div className="glass-card overflow-hidden">
          {isLoading && expenses.length === 0 ? (
            <LoadingSpinner size="md" message="Loading Expenses" submessage="Syncing records..." />
          ) : filteredExpenses.length === 0 ? (
            <p className="py-12 text-center text-muted-foreground italic">
              {searchTerm || filterCategory !== 'All' || dateFrom || dateTo
                ? 'No expenses match your filters.'
                : 'No expenses recorded yet. Click "Record Expense" to get started.'}
            </p>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="md:hidden divide-y divide-border/50">
                {filteredExpenses.map(expense => (
                  <div key={expense.id || expense._id} className="p-4 flex flex-col gap-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-foreground truncate">{expense.title}</div>
                        {expense.reference && <div className="text-xs text-muted-foreground">Ref: {expense.reference}</div>}
                        {expense.notes && <div className="text-xs text-muted-foreground truncate">{expense.notes}</div>}
                      </div>
                      <span className="font-bold text-red-400 whitespace-nowrap">Rs.{(expense.amount || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="px-2 py-0.5 bg-red-500/10 text-red-400 rounded-full font-bold uppercase">{expense.category}</span>
                      <span className="capitalize text-muted-foreground">{expense.paymentMethod || '—'}</span>
                      <span className="text-muted-foreground">{expense.date ? new Date(expense.date).toLocaleDateString() : '—'}</span>
                    </div>
                    <div className="flex gap-4 pt-1">
                      <button onClick={() => setEditingExpense(expense)} className="text-xs font-bold text-primary hover:underline">EDIT</button>
                      <button onClick={() => handleDelete(expense)} className="text-xs font-bold text-red-400 hover:underline">DELETE</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table view */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-widest">
                      <th className="px-6 py-4 font-bold">Expense</th>
                      <th className="px-6 py-4 font-bold">Category</th>
                      <th className="px-6 py-4 font-bold">Amount</th>
                      <th className="px-6 py-4 font-bold">Payment</th>
                      <th className="px-6 py-4 font-bold">Date</th>
                      <th className="px-6 py-4 font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {filteredExpenses.map(expense => (
                      <tr key={expense.id || expense._id} className="hover:bg-muted/10 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-bold text-foreground">{expense.title}</div>
                          {expense.reference && <div className="text-xs text-muted-foreground">Ref: {expense.reference}</div>}
                          {expense.notes && <div className="text-xs text-muted-foreground truncate max-w-[200px]">{expense.notes}</div>}
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-0.5 bg-red-500/10 text-red-400 rounded-full text-[10px] font-bold uppercase">{expense.category}</span>
                        </td>
                        <td className="px-6 py-4 font-bold text-red-400">
                          Rs.{(expense.amount || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm capitalize">{expense.paymentMethod || '—'}</span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {expense.date ? new Date(expense.date).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <button onClick={() => setEditingExpense(expense)} className="text-xs font-bold text-primary hover:underline">EDIT</button>
                            <button onClick={() => handleDelete(expense)} className="text-xs font-bold text-red-400 hover:underline">DELETE</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Add Modal */}
        {showAddForm && renderForm(null, handleAddExpense, () => setShowAddForm(false))}

        {/* Edit Modal */}
        {editingExpense && renderForm(editingExpense, handleEditExpense, () => setEditingExpense(null))}
      </div>
    </div>
  )
}
